import React, { useRef, useEffect } from 'react';
import 'maplibre-gl/dist/maplibre-gl.css';

// Add custom styles to ensure navigation controls are interactive
const mapStyles = `
  .maplibregl-ctrl-group {
    pointer-events: auto !important;
    z-index: 1001 !important;
  }
  .maplibregl-ctrl-zoom-in,
  .maplibregl-ctrl-zoom-out {
    pointer-events: auto !important;
    cursor: pointer !important;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const existingStyle = document.getElementById('maplibre-nav-fix');
  if (!existingStyle) {
    const style = document.createElement('style');
    style.id = 'maplibre-nav-fix';
    style.textContent = mapStyles;
    document.head.appendChild(style);
  }
}

const BLMMapContainer = ({
  basemap = 'osm',
  showStates = true,
  locatedBlm,
  vendorMarkersInView = [],
  blmFacilitiesInView = [],
  recAreaMarkersInView = [],
  onLocationSelect,
  onMarkerClick,
  onMapBoundsChange,
  showBlmPoints = false,
  showBlmNames = false,
  showVendors = false
}) => {
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerPopupRef = useRef(null);

  // Initialize map
  useEffect(() => {
    let isUnmounted = false;
    let mapInstance;
    const statesLayerIds = ['states-fill', 'states-line'];

    const initializeMap = async () => {
      const maplibregl = await import('maplibre-gl');
      if (!mapContainerRef.current || isUnmounted) return;

      // Remove previous map instance if exists
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }

      let style;

      // Choose basemap style
      if (basemap === 'usgs') {
        style = {
          version: 8,
          sources: {
            'usgs-tiles': {
              type: 'raster',
              tiles: [
                'https://basemap.nationalmap.gov/arcgis/rest/services/USGSTopo/MapServer/tile/{z}/{y}/{x}'
              ],
              tileSize: 256,
              attribution: 'USGS National Map'
            }
          },
          layers: [
            {
              id: 'usgs-tiles',
              type: 'raster',
              source: 'usgs-tiles',
              minzoom: 0,
              maxzoom: 20
            }
          ]
        };
      } else if (basemap === 'relief') {
        style = {
          version: 8,
          sources: {
            'relief-tiles': {
              type: 'raster',
              tiles: [
                'https://basemap.nationalmap.gov/arcgis/rest/services/USGSShadedReliefOnly/MapServer/tile/{z}/{y}/{x}'
              ],
              tileSize: 256,
              attribution: 'USGS Shaded Relief'
            }
          },
          layers: [
            {
              id: 'relief-tiles',
              type: 'raster',
              source: 'relief-tiles',
              minzoom: 0,
              maxzoom: 20
            }
          ]
        };
      } else if (basemap === 'osm') {
        style = {
          version: 8,
          sources: {
            'osm-tiles': {
              type: 'raster',
              tiles: [
                'https://tile.openstreetmap.org/{z}/{x}/{y}.png'
              ],
              tileSize: 256,
              attribution: '© OpenStreetMap contributors'
            }
          },
          layers: [
            {
              id: 'osm-tiles',
              type: 'raster',
              source: 'osm-tiles',
              minzoom: 0,
              maxzoom: 19
            }
          ]
        };
      } else {
        style = 'https://demotiles.maplibre.org/globe.json';
      }

      mapInstance = new maplibregl.Map({
        container: mapContainerRef.current,
        style,
        center: [-104.9903, 39.7392],
        zoom: 5
      });

      mapInstanceRef.current = mapInstance;

      // Handle map load
      mapInstance.on('load', async () => {
        if (isUnmounted) return;
        
        // Add navigation controls AFTER map is loaded to ensure they work properly
        mapInstance.addControl(new maplibregl.NavigationControl({
          showCompass: true,
          showZoom: true,
          visualizePitch: false
        }), 'top-right');
        
        // Handle states layer visibility
        for (const layerId of statesLayerIds) {
          if (mapInstance.getLayer(layerId)) {
            mapInstance.setLayoutProperty(layerId, 'visibility', showStates ? 'visible' : 'none');
          }
        }

        // Clean up existing located marker
        if (mapInstance.getLayer('located-blm-marker')) {
          mapInstance.removeLayer('located-blm-marker');
        }
        if (mapInstance.getSource('located-blm-marker')) {
          mapInstance.removeSource('located-blm-marker');
        }

        // Trigger initial bounds change to set map bounds
        if (onMapBoundsChange) {
          const bounds = mapInstance.getBounds();
          onMapBoundsChange(bounds);
        }

        // Add click handler to close popup when clicking on map background
        const handleMapClick = (e) => {
          // Only close popup if not clicking a marker layer
          const markerLayers = [
            'located-blm-marker',
            'rec-area-markers',
            'blm-facilities-markers',
            'vendor-markers'
          ];
          // If no features or features are not from marker layers, close popup
          const features = mapInstance.queryRenderedFeatures(e.point);
          const isMarker = features.some(f => markerLayers.includes(f.layer.id));
          if (!isMarker && markerPopupRef.current) {
            markerPopupRef.current.remove();
            markerPopupRef.current = null;
          }
        };
        mapInstance.on('click', handleMapClick);
      });

      // Handle map bounds change
      mapInstance.on('moveend', () => {
        if (isUnmounted || !onMapBoundsChange) return;
        const bounds = mapInstance.getBounds();
        onMapBoundsChange(bounds);
      });
    };

    initializeMap();

    return () => {
      isUnmounted = true;
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [basemap, showStates]);

  // Handle located BLM marker
  useEffect(() => {
    if (!mapInstanceRef.current || !locatedBlm) return;

    const mapInstance = mapInstanceRef.current;
    
    // Remove existing marker
    if (mapInstance.getLayer('located-blm-marker')) {
      mapInstance.removeLayer('located-blm-marker');
    }
    if (mapInstance.getSource('located-blm-marker')) {
      mapInstance.removeSource('located-blm-marker');
    }

    if (locatedBlm) {
      // Handle both BLM facilities and rec areas data structures
      const latitude = locatedBlm.latitude || locatedBlm.RecAreaLatitude;
      const longitude = locatedBlm.longitude || locatedBlm.RecAreaLongitude;
      const name = locatedBlm.name || locatedBlm.RecAreaName;
      
      if (latitude !== null && longitude !== null && latitude !== undefined && longitude !== undefined) {
        mapInstance.addSource('located-blm-marker', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: [
              {
                type: 'Feature',
                geometry: { type: 'Point', coordinates: [longitude, latitude] },
                properties: { 
                  name: name, 
                  latitude: latitude, 
                  longitude: longitude 
                }
              }
            ]
          }
        });

        mapInstance.addLayer({
          id: 'located-blm-marker',
          type: 'circle',
          source: 'located-blm-marker',
          paint: {
            'circle-radius': 10,
            'circle-color': '#3b82f6',
            'circle-stroke-width': 3,
            'circle-stroke-color': '#fff'
          }
        });

        // Fly to the marker location
        mapInstance.flyTo({ center: [longitude, latitude], zoom: 12 });

        // Add click event for marker
        mapInstance.on('click', 'located-blm-marker', (e) => {
          const feature = e.features && e.features[0];
          if (feature && onLocationSelect) {
            onLocationSelect({
              name: feature.properties.name,
              lat: feature.properties.latitude,
              lon: feature.properties.longitude
            });

            // Close previous popup if open
            if (markerPopupRef.current) {
              markerPopupRef.current.remove();
            }

            // Create popup content with name, coordinates, and Google Maps link
            const popupContent = document.createElement('div');
            popupContent.style.minWidth = '180px';
            popupContent.style.maxWidth = '240px';
            popupContent.innerHTML = `
              <div style='font-weight:600;color:#3b82f6;margin-bottom:4px;'>${feature.properties.name}</div>
              <div style='font-size:13px;color:#334155;'>Lat: ${feature.properties.latitude}</div>
              <div style='font-size:13px;color:#334155;'>Lng: ${feature.properties.longitude}</div>
              <a href='https://www.google.com/maps?q=${feature.properties.latitude},${feature.properties.longitude}' target='_blank' rel='noopener noreferrer' style='display:inline-block;margin-top:8px;font-size:13px;color:#2563eb;text-decoration:underline;'>View on Google Maps</a>
            `;

            // Create and show popup
            const popup = new (require('maplibre-gl')).Popup({
              closeButton: true,
              closeOnClick: false,
              offset: 15
            })
              .setLngLat([feature.properties.longitude, feature.properties.latitude])
              .setDOMContent(popupContent)
              .addTo(mapInstance);

            markerPopupRef.current = popup;
          }
        });

        // Change cursor on hover
        mapInstance.on('mouseenter', 'located-blm-marker', () => {
          mapInstance.getCanvas().style.cursor = 'pointer';
        });

        mapInstance.on('mouseleave', 'located-blm-marker', () => {
          mapInstance.getCanvas().style.cursor = '';
        });
      }
    }
  }, [locatedBlm, onLocationSelect]);

  // Handle vendor markers
  useEffect(() => {
    if (!mapInstanceRef.current) return;
    const mapInstance = mapInstanceRef.current;

    // Remove existing vendor markers
    if (mapInstance.getLayer('vendor-markers')) {
      mapInstance.removeLayer('vendor-markers');
    }
    if (mapInstance.getSource('vendor-markers')) {
      mapInstance.removeSource('vendor-markers');
    }

    // Add SVG image for vendor marker if not already present
    const vendorMarkerId = 'vendor-marker-svg';

    let isAddingImage = false;
    
    function addSVGImage() {
      const svgTeardrop = `
        <svg xmlns="http://www.w3.org/2000/svg" width="64" height="85" viewBox="0 0 96 128" aria-label="Outdoorec compass marker">
          <path d="M48 4
                   C28 4 12 20 12 40
                   C12 68 48 124 48 124
                   C48 124 84 68 84 40
                   C84 20 68 4 48 4Z"
                fill="none" stroke="#9333ea" stroke-width="6"/>
          <circle cx="48" cy="44" r="20" fill="white" stroke="#9333ea" stroke-width="4"/>
          <g transform="translate(48,44)" fill="#9333ea">
            <line x1="0" y1="0" x2="-12" y2="-12" stroke="#9333ea" stroke-width="3"/>
            <polygon points="-15,-15 -9,-9 -12,-12"/>
            <line x1="0" y1="0" x2="12" y2="12" stroke="#9333ea" stroke-width="3"/>
            <polygon points="15,15 9,9 12,12"/>
            <circle cx="0" cy="0" r="3" fill="#9333ea"/>
          </g>
        </svg>
      `;
      
      // Check if image exists or is currently being added
      if (!mapInstance.hasImage(vendorMarkerId) && !isAddingImage) {
        isAddingImage = true;
        const img = new window.Image();
        const svg = new Blob([svgTeardrop], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(svg);
        img.onload = () => {
          try {
            // Double-check before adding in case multiple calls happened
            if (!mapInstance.hasImage(vendorMarkerId)) {
              mapInstance.addImage(vendorMarkerId, img, { pixelRatio: 2 });
            }
          } catch (error) {
            console.warn('Failed to add vendor marker image:', error.message);
          } finally {
            isAddingImage = false;
            URL.revokeObjectURL(url);
          }
        };
        img.onerror = () => {
          isAddingImage = false;
          URL.revokeObjectURL(url);
        };
        img.src = url;
      }
    }

    // Add SVG image initially and on style reloads
    addSVGImage();
    // Remove previous styledata listener if present
    mapInstance.off && mapInstance.off('styledata', addSVGImage);
    mapInstance.on('styledata', addSVGImage);

    if (vendorMarkersInView.length > 0 && showVendors) {
      const features = vendorMarkersInView.map(item => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [item.longitude, item.latitude] },
        properties: {
          vendor_name: item.vendor_name,
          latitude: item.latitude,
          longitude: item.longitude,
          rating: item.rating,
          website: item.website,
          formatted_address: item.formatted_address,
          international_phone_number: item.international_phone_number,
          category: item.category,
          state: item.state,
          content: item.content
        }
      }));

      mapInstance.addSource('vendor-markers', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features
        }
      });

      // Add vendor markers as symbol layer with SVG
      mapInstance.addLayer({
        id: 'vendor-markers',
        type: 'symbol',
        source: 'vendor-markers',
        layout: {
          'icon-image': vendorMarkerId,
          'icon-size': 1.2,
          'icon-anchor': 'bottom',
          'icon-allow-overlap': true
        }
      });

      // Add click handlers for vendor markers
      mapInstance.on('click', 'vendor-markers', (e) => {
        const feature = e.features && e.features[0];
        if (feature) {
          if (onMarkerClick) {
            onMarkerClick({
              vendor_name: feature.properties.vendor_name,
              name: feature.properties.vendor_name,
              latitude: feature.properties.latitude,
              longitude: feature.properties.longitude,
              rating: feature.properties.rating,
              website: feature.properties.website,
              formatted_address: feature.properties.formatted_address,
              international_phone_number: feature.properties.international_phone_number,
              category: feature.properties.category,
              state: feature.properties.state,
              content: feature.properties.content
            });
          }
          if (onLocationSelect) {
            onLocationSelect({
              name: feature.properties.vendor_name,
              lat: feature.properties.latitude,
              lon: feature.properties.longitude
            });
          }
          if (markerPopupRef.current) {
            markerPopupRef.current.remove();
          }
          const popupContent = document.createElement('div');
          popupContent.style.minWidth = '180px';
          popupContent.style.maxWidth = '240px';
          popupContent.innerHTML = `
            <div style='font-weight:600;color:#9333ea;margin-bottom:4px;'>${feature.properties.vendor_name}</div>
            <div style='font-size:13px;color:#334155;'>Lat: ${feature.properties.latitude}</div>
            <div style='font-size:13px;color:#334155;'>Lng: ${feature.properties.longitude}</div>
            <a href='https://www.google.com/maps?q=${feature.properties.latitude},${feature.properties.longitude}' target='_blank' rel='noopener noreferrer' style='display:inline-block;margin-top:8px;font-size:13px;color:#2563eb;text-decoration:underline;'>View on Google Maps</a>
          `;
          const popup = new (require('maplibre-gl')).Popup({
            closeButton: true,
            closeOnClick: false,
            offset: 15
          })
            .setLngLat([feature.properties.longitude, feature.properties.latitude])
            .setDOMContent(popupContent)
            .addTo(mapInstance);
          markerPopupRef.current = popup;
        }
      });

      mapInstance.on('mouseenter', 'vendor-markers', () => {
        mapInstance.getCanvas().style.cursor = 'pointer';
      });

      mapInstance.on('mouseleave', 'vendor-markers', () => {
        mapInstance.getCanvas().style.cursor = '';
      });
    }
    
    // Cleanup event handlers on unmount or dependency change
    return () => {
      if (mapInstance && mapInstance.off) {
        mapInstance.off('click', 'vendor-markers');
        mapInstance.off('mouseenter', 'vendor-markers');
        mapInstance.off('mouseleave', 'vendor-markers');
      }
    };
  }, [vendorMarkersInView, showVendors, onLocationSelect]);

  // Handle recreation area markers
  useEffect(() => {
    if (!mapInstanceRef.current) return;

    const mapInstance = mapInstanceRef.current;

    // Remove existing rec area markers
    if (mapInstance.getLayer('rec-area-markers')) {
      mapInstance.removeLayer('rec-area-markers');
    }
    if (mapInstance.getSource('rec-area-markers')) {
      mapInstance.removeSource('rec-area-markers');
    }

    if (recAreaMarkersInView.length > 0 && showBlmNames) {
      const features = recAreaMarkersInView.map(item => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [item.RecAreaLongitude, item.RecAreaLatitude] },
        properties: {
          id: item.id || item.RecAreaID || `${item.RecAreaLatitude}_${item.RecAreaLongitude}`,
          name: item.RecAreaName || item.name,
          latitude: item.RecAreaLatitude || item.latitude,
          longitude: item.RecAreaLongitude || item.longitude,
          description: item.RecAreaDescription || item.description || 'No description available',
          state: item.RecAreaState || item.state
        }
      }));

      mapInstance.addSource('rec-area-markers', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features
        }
      });

      mapInstance.addLayer({
        id: 'rec-area-markers',
        type: 'circle',
        source: 'rec-area-markers',
        paint: {
          'circle-radius': 6,
          'circle-color': '#059669',
          'circle-stroke-width': 2,
          'circle-stroke-color': '#fff'
        }
      });

      // Add click handlers for rec area markers
      mapInstance.on('click', 'rec-area-markers', (e) => {
        const feature = e.features && e.features[0];
        if (feature) {
          // Set current selection in sidebar via new marker click handler
          if (onMarkerClick) {
            onMarkerClick({
              id: feature.properties.id,
              name: feature.properties.name,
              RecAreaName: feature.properties.name,
              RecAreaLatitude: feature.properties.latitude,
              RecAreaLongitude: feature.properties.longitude,
              RecAreaDescription: feature.properties.description,
              RecAreaState: feature.properties.state,
              latitude: feature.properties.latitude,
              longitude: feature.properties.longitude,
              description: feature.properties.description,
              state: feature.properties.state
            });
          }
          
          // Also call legacy onLocationSelect for backward compatibility
          if (onLocationSelect) {
            onLocationSelect({
              name: feature.properties.name,
              lat: feature.properties.latitude,
              lon: feature.properties.longitude
            });
          }

          // Close previous popup if open
          if (markerPopupRef.current) {
            markerPopupRef.current.remove();
          }
          // Create popup content
          const popupContent = document.createElement('div');
          popupContent.style.minWidth = '180px';
          popupContent.style.maxWidth = '240px';
          popupContent.innerHTML = `
            <div style='font-weight:600;color:#059669;margin-bottom:4px;'>${feature.properties.name}</div>
            <div style='font-size:13px;color:#334155;'>Lat: ${feature.properties.latitude}</div>
            <div style='font-size:13px;color:#334155;'>Lng: ${feature.properties.longitude}</div>
            <a href='https://www.google.com/maps?q=${feature.properties.latitude},${feature.properties.longitude}' target='_blank' rel='noopener noreferrer' style='display:inline-block;margin-top:8px;font-size:13px;color:#2563eb;text-decoration:underline;'>View on Google Maps</a>
          `;
          const popup = new (require('maplibre-gl')).Popup({
            closeButton: true,
            closeOnClick: false,
            offset: 15
          })
            .setLngLat([feature.properties.longitude, feature.properties.latitude])
            .setDOMContent(popupContent)
            .addTo(mapInstance);
          markerPopupRef.current = popup;
        }
      });

      mapInstance.on('mouseenter', 'rec-area-markers', () => {
        mapInstance.getCanvas().style.cursor = 'pointer';
      });

      mapInstance.on('mouseleave', 'rec-area-markers', () => {
        mapInstance.getCanvas().style.cursor = '';
      });
    }
    
    // Cleanup event handlers on unmount or dependency change
    return () => {
      if (mapInstance && mapInstance.off) {
        mapInstance.off('click', 'rec-area-markers');
        mapInstance.off('mouseenter', 'rec-area-markers');
        mapInstance.off('mouseleave', 'rec-area-markers');
      }
    };
  }, [recAreaMarkersInView, showBlmNames, onLocationSelect]);

  // Handle BLM facilities markers (different from rec areas)
  useEffect(() => {
    if (!mapInstanceRef.current) return;

    const mapInstance = mapInstanceRef.current;

    // Remove existing BLM facilities markers
    if (mapInstance.getLayer('blm-facilities-markers')) {
      mapInstance.removeLayer('blm-facilities-markers');
    }
    if (mapInstance.getSource('blm-facilities-markers')) {
      mapInstance.removeSource('blm-facilities-markers');
    }

    // Show BLM facilities markers when showBlmPoints is true
    if (blmFacilitiesInView.length > 0 && showBlmPoints) {
      const features = blmFacilitiesInView
        .filter(item => item.longitude && item.latitude && 
                       !isNaN(item.longitude) && !isNaN(item.latitude))
        .map(item => ({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [item.longitude, item.latitude] },
          properties: {
            id: item.id || `${item.latitude}_${item.longitude}`,
            name: item.name || 'Unnamed Facility',
            latitude: item.latitude,
            longitude: item.longitude,
            description: item.description || item.facility_subtype || 'No description available',
            state: item.state,
            facility_subtype: item.facility_subtype
          }
        }));

      if (features.length > 0) {
        mapInstance.addSource('blm-facilities-markers', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features
          }
        });

        mapInstance.addLayer({
          id: 'blm-facilities-markers',
          type: 'circle',
          source: 'blm-facilities-markers',
          paint: {
            'circle-radius': 5,
            'circle-color': '#3b82f6', // Blue color for BLM facilities
            'circle-stroke-width': 2,
            'circle-stroke-color': '#fff'
          }
        });

        // Add click handlers for BLM facilities markers
        mapInstance.on('click', 'blm-facilities-markers', (e) => {
          const feature = e.features && e.features[0];
          if (feature) {
            // Set current selection in sidebar via new marker click handler
            if (onMarkerClick) {
              onMarkerClick({
                id: feature.properties.id,
                name: feature.properties.name,
                latitude: feature.properties.latitude,
                longitude: feature.properties.longitude,
                description: feature.properties.description,
                state: feature.properties.state,
                facility_subtype: feature.properties.facility_subtype
              });
            }
            
            // Also call legacy onLocationSelect for backward compatibility
            if (onLocationSelect) {
              onLocationSelect({
                name: feature.properties.name,
                lat: feature.properties.latitude,
                lon: feature.properties.longitude
              });
            }

            // Close previous popup if open
            if (markerPopupRef.current) {
              markerPopupRef.current.remove();
            }
            // Create popup content
            const popupContent = document.createElement('div');
            popupContent.style.minWidth = '180px';
            popupContent.style.maxWidth = '240px';
            popupContent.innerHTML = `
              <div style='font-weight:600;color:#3b82f6;margin-bottom:4px;'>${feature.properties.name}</div>
              <div style='font-size:13px;color:#334155;'>Lat: ${feature.properties.latitude}</div>
              <div style='font-size:13px;color:#334155;'>Lng: ${feature.properties.longitude}</div>
              <a href='https://www.google.com/maps?q=${feature.properties.latitude},${feature.properties.longitude}' target='_blank' rel='noopener noreferrer' style='display:inline-block;margin-top:8px;font-size:13px;color:#2563eb;text-decoration:underline;'>View on Google Maps</a>
            `;
            const popup = new (require('maplibre-gl')).Popup({
              closeButton: true,
              closeOnClick: false,
              offset: 15
            })
              .setLngLat([feature.properties.longitude, feature.properties.latitude])
              .setDOMContent(popupContent)
              .addTo(mapInstance);
            markerPopupRef.current = popup;
          }
        });

        mapInstance.on('mouseenter', 'blm-facilities-markers', () => {
          mapInstance.getCanvas().style.cursor = 'pointer';
        });

        mapInstance.on('mouseleave', 'blm-facilities-markers', () => {
          mapInstance.getCanvas().style.cursor = '';
        });
      }
    }
    
    // Cleanup event handlers on unmount or dependency change
    return () => {
      if (mapInstance && mapInstance.off) {
        mapInstance.off('click', 'blm-facilities-markers');
        mapInstance.off('mouseenter', 'blm-facilities-markers');
        mapInstance.off('mouseleave', 'blm-facilities-markers');
      }
    };
  }, [blmFacilitiesInView, showBlmPoints, onLocationSelect]);

  return (
    <div style={{ flex: 1, position: 'relative' }}>
      <div 
        ref={mapContainerRef} 
        style={{ 
          width: '100%', 
          height: '100%',
          pointerEvents: 'auto' // Ensure map container can receive pointer events
        }}
      ></div>
    </div>
  );
};

export default BLMMapContainer;